import { Container, FillGradient, Graphics, Sprite, Ticker } from 'pixi.js';
import { BookConstants } from '@/constants/bookConstants';
import { buildFoldEdgeShadowPath } from '../calc/foldEdgeShadow';
import { normalizePageRadius, type PageCornerSide } from '../display/pageRadius';
import { createLinearGradientTexture } from '../display/shadows';
import type { CornerShadowSide, FlipCorner, Point, Segment } from '@/types/book';
import type { ImageLoadFailureKind } from '@/types/canvas-type';
import { buildPageErrorContent } from './pageErrorLayer';

const { CORNER_SHADOW_SIZE_RATIO, CORNER_SHADOW_MAX_SIZE } = BookConstants;
const FLIPPING_PAGE_EDGE_WIDTH = 1;
const FLIPPING_PAGE_EDGE_STYLE = {
  color: 0xaaaaaa,
  alpha: 0.3,
  width: FLIPPING_PAGE_EDGE_WIDTH,
  alignment: 0
} as const;

export class PixiPage {
  public index: number;
  readonly width: number;
  readonly height: number;
  /** 页面是否需要绘制书脊与角落阴影，默认开启。 */
  public needSpineShadow = true;

  readonly root = new Container();
  // 变换容器：页面旋转等几何变换作用在这里。
  readonly container = new Container();
  // 页面视觉总容器：统一承载页面内容与页内效果，并应用外侧圆角遮罩。
  private readonly visualContainer = new Container();
  // 内容容器：上层组件或业务代码向这里添加页面内容。
  readonly content = new Container();
  // 页面白色底板，铺满整页，防止内容未铺满时透出下层。
  private readonly background = new Graphics();
  // 翻页角提示阴影非常淡，用于白色页面上提示可从顶部角落翻页。
  readonly cornerShadow = new Graphics();
  // 书脊阴影位于内容之上，用来增强纸张贴近书脊的厚度感。
  readonly spineShadow = new Sprite();
  private readonly flippingPageEdge = new Graphics();
  readonly maskGraphics = new Graphics();
  private readonly roundedMask = new Graphics();
  // 内容尚未加载完成时显示的加载提示层，覆盖在内容之上。
  readonly loadingLayer = new Container();
  private readonly loadingSpinner = new Graphics();
  private spinning = false;
  // 插画加载失败时显示的失败层；构造时为空，首次 showError 才懒构建。
  readonly errorLayer = new Container();
  private errorBuiltKind: ImageLoadFailureKind | null = null;
  private errorKind: ImageLoadFailureKind | null = null;
  private errorBuildingKind: ImageLoadFailureKind | null = null;
  private errorBuildToken = 0;
  private area: Point[] = [];
  private readonly pageRadius: number;
  private readonly onErrorEdit?: () => void;
  private readonly onErrorRetry?: () => void;
  private errorEditLabel?: string;

  /** 创建一个 Pixi 页面，并建立内容层、阴影层和遮罩层的固定结构。 */
  constructor(
    _index: number,
    _width: number,
    _height: number,
    _pageRadius?: number,
    _onErrorEdit?: () => void,
    _errorEditLabel?: string,
    _onErrorRetry?: () => void
  ) {
    this.index = _index;
    this.width = _width;
    this.height = _height;
    this.pageRadius = normalizePageRadius(_pageRadius, this.width, this.height);
    this.onErrorEdit = _onErrorEdit;
    this.onErrorRetry = _onErrorRetry;
    this.errorEditLabel = _errorEditLabel;
    this.root.label = `page-${this.index}`;
    this.container.label = `page-${this.index}-transform`;
    this.visualContainer.label = `page-${this.index}-visual`;
    this.content.label = `page-${this.index}-content`;
    this.cornerShadow.label = `page-${this.index}-corner-shadow`;
    // 纯装饰层不能参与命中测试，否则它的大三角形会遮住下方内容的点击区域。
    this.cornerShadow.eventMode = 'none';
    this.spineShadow.label = `page-${this.index}-spine-shadow`;
    this.flippingPageEdge.label = `page-${this.index}-flipping-edge`;
    this.maskGraphics.label = `page-${this.index}-mask`;
    this.roundedMask.label = `page-${this.index}-rounded-mask`;
    this.loadingLayer.label = `page-${this.index}-loading`;
    this.errorLayer.label = `page-${this.index}-error`;
    this.background.label = `page-${this.index}-background`;
    this.background.rect(0, 0, this.width, this.height).fill({ color: 0xffffff, alpha: 1 });
    this.visualContainer.addChild(this.background);
    this.visualContainer.addChild(this.content);
    this.visualContainer.addChild(this.errorLayer);
    this.visualContainer.addChild(this.cornerShadow);
    this.visualContainer.addChild(this.spineShadow);
    this.visualContainer.addChild(this.loadingLayer);
    this.errorLayer.visible = false;
    this.container.addChild(this.visualContainer);
    this.container.addChild(this.roundedMask);
    this.container.addChild(this.flippingPageEdge);
    this.root.addChild(this.container);
    this.root.addChild(this.maskGraphics);
    this.root.mask = this.maskGraphics;
    this.setFullArea();
    this.setupLoadingLayer();
  }

  /** 仅裁剪页面远离书脊一侧的两个外角。 */
  setupRoundedCorners(side: PageCornerSide): void {
    this.roundedMask.clear();
    this.flippingPageEdge.clear();

    this.drawOuterPagePath(this.flippingPageEdge, side);
    this.flippingPageEdge.stroke(FLIPPING_PAGE_EDGE_STYLE);
    this.flippingPageEdge.visible = false;

    if (this.pageRadius <= 0) {
      this.visualContainer.mask = null;
      return;
    }

    this.drawOuterPagePath(this.roundedMask, side);
    this.roundedMask.closePath();
    this.roundedMask.fill({ color: 0xffffff });
    this.visualContainer.mask = this.roundedMask;
  }

  /** 绘制上边、外侧边和下边组成的开放路径，不闭合书脊侧。 */
  private drawOuterPagePath(graphics: Graphics, side: PageCornerSide): void {
    const radius = this.pageRadius;
    if (side === 'left') {
      graphics.moveTo(this.width, 0);
      graphics.lineTo(radius, 0);
      if (radius > 0) graphics.quadraticCurveTo(0, 0, 0, radius);
      graphics.lineTo(0, this.height - radius);
      if (radius > 0) graphics.quadraticCurveTo(0, this.height, radius, this.height);
      graphics.lineTo(this.width, this.height);
    } else {
      graphics.moveTo(0, 0);
      graphics.lineTo(this.width - radius, 0);
      if (radius > 0) graphics.quadraticCurveTo(this.width, 0, this.width, radius);
      graphics.lineTo(this.width, this.height - radius);
      if (radius > 0)
        graphics.quadraticCurveTo(this.width, this.height, this.width - radius, this.height);
      graphics.lineTo(0, this.height);
    }
  }

  /** 构建加载提示层：虚线圆环（按角度渐变透明度），默认隐藏，不填充背景。 */
  private setupLoadingLayer(): void {
    const radius = Math.max(8, Math.min(this.width, this.height) * 0.04);
    const segmentCount = 12;
    const gapRatio = 0.4;
    const segmentAngle = (Math.PI * 2) / segmentCount;
    const dashAngle = segmentAngle * (1 - gapRatio);

    for (let i = 0; i < segmentCount; i += 1) {
      const startAngle = segmentAngle * i;
      const alpha = 0.12 + 0.78 * (i / (segmentCount - 1));
      this.loadingSpinner
        .arc(0, 0, radius, startAngle, startAngle + dashAngle)
        .stroke({ width: radius * 0.18, color: 0x9a9a9a, alpha, cap: 'round' });
    }
    this.loadingSpinner.position.set(this.width / 2, this.height / 2);

    this.loadingLayer.addChild(this.loadingSpinner);
    this.loadingLayer.visible = false;
  }

  /** 旋转动画每帧回调，保持 spinner 持续转动。 */
  private readonly spinTick = (ticker: Ticker): void => {
    this.loadingSpinner.rotation += 0.008 * ticker.deltaMS;
  };

  /** 显示加载提示层，用于内容尚未加入时占位。 */
  showLoading(): void {
    this.hideError();
    this.loadingLayer.visible = true;
    if (!this.spinning) {
      this.spinning = true;
      Ticker.shared.add(this.spinTick);
    }
  }

  /** 隐藏加载提示层，内容加入后调用。 */
  hideLoading(): void {
    this.loadingLayer.visible = false;
    if (this.spinning) {
      this.spinning = false;
      Ticker.shared.remove(this.spinTick);
    }
  }

  /** 显示失败层；首次调用才懒构建内容。 */
  private rebuildErrorContent(kind: ImageLoadFailureKind): void {
    const buildToken = ++this.errorBuildToken;
    this.errorBuildingKind = kind;
    void buildPageErrorContent(this.errorLayer, this.width, this.height, {
      kind,
      onRetry: this.onErrorRetry,
      onEdit: this.onErrorEdit,
      editLabel: this.errorEditLabel
    })
      .then(() => {
        if (buildToken === this.errorBuildToken) {
          this.errorBuiltKind = kind;
        }
      })
      .finally(() => {
        if (buildToken === this.errorBuildToken) {
          this.errorBuildingKind = null;
        }
      });
  }

  showError(kind: ImageLoadFailureKind): void {
    this.hideLoading();
    this.errorKind = kind;
    this.errorLayer.visible = true;
    if (this.errorBuiltKind !== kind && this.errorBuildingKind !== kind) {
      this.rebuildErrorContent(kind);
    }
  }

  setErrorEditLabel(label?: string): void {
    if (this.errorEditLabel === label) return;

    this.errorEditLabel = label;
    if (this.errorKind === 'permanent') {
      this.errorBuiltKind = null;
      this.rebuildErrorContent('permanent');
    }
  }

  /** 隐藏失败层，不销毁已建内容。 */
  hideError(): void {
    this.errorLayer.visible = false;
  }

  /** 按页面所在位置设置书脊侧阴影。 */
  setupSpineShadow(side: 'left' | 'right' | 'first' | 'last'): void {
    if (!this.needSpineShadow) {
      this.spineShadow.visible = false;
      this.cornerShadow.visible = false;
      this.cornerShadow.clear();
      return;
    }

    let stops: Array<[number, string]>;
    let shadowWidth: number;
    switch (side) {
      case 'left':
        stops = [
          [0, 'rgba(200,200,200,0)'],
          [0.6, 'rgba(50,50,50,0.2)'],
          [1, 'rgba(60,60,60,0.5)']
        ] as Array<[number, string]>;
        shadowWidth = this.width / 4;
        break;
      case 'right':
        stops = [
          [0, 'rgba(53,53,53,0.57)'],
          [0.4, 'rgba(53,53,53,0.2)'],
          [0.6, 'rgba(53,53,53,0.1)'],
          [1, 'rgba(53,53,53,0)']
        ] as Array<[number, string]>;
        shadowWidth = this.width / 10;
        break;
      case 'first':
        stops = [
          [0, 'rgba(0,0,0,0.4)'],
          [1, 'rgba(0,0,0,0)']
        ] as Array<[number, string]>;
        shadowWidth = this.width / 56;
        break;
      case 'last':
        stops = [
          [0, 'rgba(0,0,0,0)'],
          [1, 'rgba(0,0,0,0.4)']
        ] as Array<[number, string]>;
        shadowWidth = this.width / 56;
        break;
    }

    this.spineShadow.visible = true;
    this.spineShadow.texture = createLinearGradientTexture(stops);
    this.spineShadow.position.set(
      side === 'left' || side === 'last' ? this.width - shadowWidth : 0,
      0
    );
    this.spineShadow.scale.set(
      shadowWidth / this.spineShadow.texture.width,
      this.height / this.spineShadow.texture.height
    );
    this.setupCornerShadow(side === 'left' || side === 'last' ? 'left' : 'right');
  }

  /** 根据页面所在跨页区域，在外侧上下角落绘制极淡的翻页提示阴影。 */
  private setupCornerShadow(side: CornerShadowSide): void {
    const size = Math.round(
      Math.min(CORNER_SHADOW_MAX_SIZE, Math.min(this.width, this.height) * CORNER_SHADOW_SIZE_RATIO)
    );

    // 顶部角落渐变：从外上角向内下角扩散
    const topGradientCenterX = side === 'left' ? 0 : 1;
    const topGradient = new FillGradient({
      type: 'radial',
      center: { x: topGradientCenterX, y: 0 },
      innerRadius: 0,
      outerCenter: { x: topGradientCenterX, y: 0 },
      outerRadius: 0.75,
      colorStops: [
        { offset: 0, color: 'rgba(38, 34, 30, 0.22)' },
        { offset: 0.42, color: 'rgba(38, 34, 30, 0.024)' },
        { offset: 1, color: 'rgba(38, 34, 30, 0)' }
      ]
    });

    // 底部角落渐变：从外下角向内上角扩散
    const bottomGradientCenterX = side === 'left' ? 0 : 1;
    const bottomGradient = new FillGradient({
      type: 'radial',
      center: { x: bottomGradientCenterX, y: 1 },
      innerRadius: 0,
      outerCenter: { x: bottomGradientCenterX, y: 1 },
      outerRadius: 0.75,
      colorStops: [
        { offset: 0, color: 'rgba(38, 34, 30, 0.15)' },
        { offset: 0.42, color: 'rgba(38, 34, 30, 0.024)' },
        { offset: 1, color: 'rgba(38, 34, 30, 0)' }
      ]
    });

    this.cornerShadow.visible = true;
    this.cornerShadow.clear();

    const isLeft = side === 'left';

    // 步骤1：绘制顶部角落三角形
    const topCornerX = isLeft ? 0 : size;
    const topEdgeX = isLeft ? size : 0;
    this.cornerShadow.moveTo(topCornerX, 0);
    this.cornerShadow.lineTo(topEdgeX, 0);
    this.cornerShadow.lineTo(topCornerX, size);
    this.cornerShadow.closePath();
    this.cornerShadow.fill(topGradient);

    // 步骤2：绘制底部角落三角形
    const bottomCornerX = isLeft ? 0 : size;
    const bottomEdgeX = isLeft ? size : 0;
    this.cornerShadow.moveTo(bottomCornerX, this.height);
    this.cornerShadow.lineTo(bottomEdgeX, this.height);
    this.cornerShadow.lineTo(bottomCornerX, this.height - size);
    this.cornerShadow.closePath();
    this.cornerShadow.fill(bottomGradient);

    this.cornerShadow.position.set(isLeft ? 0 : this.width - size, 0);
  }

  /** 设置页面当前可见多边形区域，翻页时用于裁剪页面内容。 */
  setArea(
    points: Point[],
    showFoldEdgeShadow = false,
    foldCorner: FlipCorner = 'bottom',
    foldCreaseSegment?: Segment
  ): void {
    if (points.length < 3) return;

    this.area = points;
    this.maskGraphics.clear();
    this.flippingPageEdge.visible = showFoldEdgeShadow;

    const polygon: number[] = [];
    for (const point of this.area) {
      polygon.push(point.x, point.y);
    }

    this.maskGraphics.poly(polygon);
    this.maskGraphics.fill({ color: 0xffffff });
    this.expandMaskForFlippingPageEdge(showFoldEdgeShadow, foldCorner, foldCreaseSegment);
  }

  /** 将页面遮罩恢复为完整矩形区域。 */
  setFullArea(): void {
    this.area = [
      { x: 0, y: 0 },
      { x: this.width, y: 0 },
      { x: this.width, y: this.height },
      { x: 0, y: this.height }
    ];
    this.maskGraphics.clear();
    this.flippingPageEdge.visible = false;
    this.maskGraphics.rect(0, 0, this.width, this.height);
    this.maskGraphics.fill({ color: 0xffffff });
  }

  /** 设置页面根容器的位置。 */
  setPosition(point: Point): void {
    this.root.position.set(point.x, point.y);
  }

  /** 设置页面内容容器的旋转角度。 */
  setAngle(radians: number): void {
    this.container.rotation = radians;
  }

  /** 重置页面位置、旋转和可见区域，回到静态展示状态。 */
  resetTransform(): void {
    this.root.position.set(0, 0);
    this.root.rotation = 0;
    this.root.scale.set(1, 1);
    this.root.visible = true;
    this.container.position.set(0, 0);
    this.container.rotation = 0;
    this.setFullArea();
  }

  /** 仅沿页面外边扩展动态遮罩，避免向外描边被裁掉，同时不扩大折痕侧。 */
  private expandMaskForFlippingPageEdge(
    visible: boolean,
    _corner: FlipCorner,
    foldCreaseSegment?: Segment
  ): void {
    if (!visible) return;

    const path = buildFoldEdgeShadowPath(this.area, foldCreaseSegment);
    if (path.length < 2) return;

    this.maskGraphics.moveTo(path[0].x, path[0].y);
    for (let index = 1; index < path.length; index += 1) {
      this.maskGraphics.lineTo(path[index].x, path[index].y);
    }
    this.maskGraphics.stroke({
      color: 0xffffff,
      width: FLIPPING_PAGE_EDGE_WIDTH * 2,
      alignment: 0.5
    });
  }
}
